import '@/assets/css/main.css';
import Preset from '@/assets/presets';
import { useAppleLogin } from '@/composables/useAppleLogin';
import { useGoogleLogin } from '@/composables/useGoogleLogin';
import * as Sentry from '@sentry/vue';
import 'primeicons/primeicons.css';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { createGtag } from 'vue-gtag';
import { useAppStore } from './stores/appStore';

declare global {
  interface Window {
    __initialLanguage: string;
  }
}

import App from '@/App.vue';
import i18n from '@/i18n';
import router from '@/router';
import { createPinia } from 'pinia';
import ConfirmationService from 'primevue/confirmationservice';
import { createApp } from 'vue';

import { usePlatform } from '@/composables/usePlatform';
import { useEnvStore } from '@/stores/envStore';
import { measurePerformance } from '@/utils/performance';
import { primeLocaleByCode } from '@/utils/primevue-locale/locale';
import { Capacitor } from '@capacitor/core';
import Tooltip from 'primevue/tooltip';
// import { SplashScreen } from '@capacitor/splash-screen';

// Initialize language before app creation
function initializeLanguage() {
  const savedLanguage = localStorage.getItem('language');
  const browserLanguage = navigator.language || navigator.languages[0];
  const languageCode = browserLanguage.split('-')[0];
  const supportedLanguages = ['en', 'fr', 'de', 'it'];

  let language = 'en'; // Default

  if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
    language = savedLanguage;
  } else if (supportedLanguages.includes(languageCode)) {
    language = languageCode;
  }

  // Set HTML lang attribute immediately
  document.documentElement.lang = language;

  // Store for later use after app initialization
  window.__initialLanguage = language;
}

// Call this function before creating the Vue app
initializeLanguage();

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

// NOW we can safely use stores after Pinia is initialized
const envStore = useEnvStore();

if (envStore.environment !== 'local') {
  Sentry.init({
    app,
    dsn: 'https://76a9c39bed65893ce8a4eab5fb575972@o4507153118199808.ingest.de.sentry.io/4507247918448720',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% des transactions en production
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/staging-gastronomia-frontend.jcloud.ik-server.com/, /^http:\/\/dev-app-gastronomia.backtik.ch/],
    // Session Replay
    replaysSessionSampleRate: 0.05, // Capture 5% des sessions en production
    replaysOnErrorSampleRate: 1.0, // Capturer 100% des sessions avec erreurs
  });
}

app.use(router);
app.directive('tooltip', Tooltip);

const gtag = createGtag({
  tagId: envStore.googleAnalyticsMeasurementId,
});

// Add Google Analytics
if (envStore.environment === 'production' && !Capacitor.isNativePlatform()) {
  app.use(gtag);
}

const initialLang = window.__initialLanguage || 'en';
app.use(PrimeVue, {
  unstyled: true,
  pt: Preset,
  locale: primeLocaleByCode[initialLang],
  zIndex: {
    modal: 1100,
    overlay: 1200,
    menu: 1300,
    tooltip: 1400,
  },
});
app.use(ConfirmationService);
app.use(i18n);
app.use(ToastService);

app.mount('#app');

// Initialize platform-specific features AFTER app mount
setTimeout(async () => {
  const appStore = useAppStore();
  if (window.__initialLanguage) {
    appStore.setLanguage(window.__initialLanguage as 'en' | 'fr' | 'de' | 'it');
  }

  // Initialize platform features
  const { initPlatform } = usePlatform();
  await measurePerformance('startup:main.initPlatform', () => initPlatform());

  // Initialize Capgo Social Login for Google
  const { initialize: initializeGoogleLogin } = useGoogleLogin();
  try {
    await measurePerformance('startup:main.initializeGoogleLogin', () =>
      initializeGoogleLogin(
        envStore.googleClientId,
        envStore.googleIOSClientId,
      ),
    );
    console.log('[main.ts] Google Social Login initialized successfully');
  } catch (error) {
    console.error('[main.ts] Failed to initialize Google Social Login:', error);
  }

  // Initialize Capgo Social Login for Apple
  const { initialize: initializeAppleLogin } = useAppleLogin();
  try {
    await measurePerformance('startup:main.initializeAppleLogin', () => initializeAppleLogin());
    console.log('[main.ts] Apple Social Login initialized successfully');
  } catch (error) {
    console.error('[main.ts] Failed to initialize Apple Social Login:', error);
  }

  // Initialize mobile-specific features if running on native platform
  if (Capacitor.isNativePlatform()) {
    console.log('Running on native platform');

    // Hide splash screen
    // try {
    //   await SplashScreen.hide();
    // } catch (error) {
    //   console.log('SplashScreen plugin not available');
    // }

  }
}, 0);
